import type {
  DayPlan,
  FreeResult,
  InterpretationTrap,
  PerspectiveReportContent,
  WeekPlan,
} from '../../lib/apiClient';
import { PERSPECTIVE_PATTERN_LABELS } from '../../lib/assessment/content';
import {
  PS_HIDDEN_DEMAND,
  PS_OLD_LENS,
  PS_OPERATING_STATEMENTS,
  PS_WATCH_FOR_NOTES,
} from '../../lib/report/perspectiveReportContent';
import {
  PS_CONSCIOUS_VS_SUBCONSCIOUS,
  PS_MINDSET_FRAMING,
  PS_SCREEN_INTERACTION_NOTE,
  PS_SECONDARY_AS_MODIFIER,
  PS_TRAP_PAGE_INTRO,
} from '../../lib/report/perspectivePatternContent';
import { SevenDayWorkbook, ThirtyDayWorkbook } from './Workbook';

/**
 * The full paid Perspective report, copied from production's
 * `perspective-report.tsx`.
 *
 * **Production has thirteen sections and V2 had nine.** Four were missing
 * outright — The Demand Behind the Pattern, Secondary Pattern and Combined
 * Effect, Your Operating Statement, and the Cognitive Pattern Profile — and the
 * rest carried V2's own sentence-case titles.
 *
 * Three of the four missing sections need per-pattern copy that lives in
 * production's client rather than its API; it is extracted verbatim into
 * `lib/report/perspectiveReportContent.ts`. Everything else comes from the
 * payload V2 already receives.
 */
export function PerspectiveReport({
  result,
  content,
  interpretationTraps,
  sevenDayReset,
  thirtyDayPractice,
  disclaimer,
}: {
  result: FreeResult;
  content: PerspectiveReportContent;
  interpretationTraps: InterpretationTrap[];
  sevenDayReset: DayPlan[];
  thirtyDayPractice: WeekPlan[];
  disclaimer: string;
}) {
  const { abcdeExample } = content;
  const slug = result.typeSlug ?? '';
  const hiddenDemand = PS_HIDDEN_DEMAND[slug];
  const oldLens = PS_OLD_LENS[slug];
  const operatingStatement = PS_OPERATING_STATEMENTS[slug];
  const watchFor = PS_WATCH_FOR_NOTES[slug];

  const secondaryKey = result.secondaryPatternKey ?? null;
  const secondaryLabel = secondaryKey
    ? (PERSPECTIVE_PATTERN_LABELS[secondaryKey] ?? secondaryKey)
    : null;

  const ranked = Object.entries(result.categoryScores)
    .map(([key, score]) => ({ key, label: PERSPECTIVE_PATTERN_LABELS[key] ?? key, score }))
    .sort((a, b) => b.score - a.score);

  return (
    <div className="flex flex-col gap-10" data-testid="full-report-perspective">
      <section
        className="flex flex-col gap-4 rounded-xl border border-primary/20 bg-primary/5 p-8"
        data-testid="perspective-report-header"
      >
        <p className="text-xs font-bold uppercase tracking-widest text-primary">
          Full Perspective Report
        </p>
        <h1 className="text-3xl font-bold">{result.primaryType}</h1>
        {result.isBlended ? (
          <span className="w-fit rounded-md border border-primary/30 px-2 py-0.5 text-xs text-primary">
            Blended pattern
          </span>
        ) : null}
      </section>

      {ranked.length > 0 ? (
        <Section title="Cognitive Pattern Profile">
          <div className="flex flex-col gap-3" data-testid="perspective-pattern-profile">
            {ranked.map((p) => (
              <div key={p.key} className="flex items-center gap-3" data-pattern={p.key}>
                <span className="w-48 shrink-0 text-sm">{p.label}</span>
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-secondary">
                  <div
                    className="h-full rounded-full bg-primary"
                    style={{ width: `${Math.round((p.score / 5) * 100)}%` }}
                  />
                </div>
                <span className="w-10 shrink-0 text-right text-sm tabular-nums text-muted-foreground">
                  {p.score.toFixed(2)}
                </span>
              </div>
            ))}
          </div>
        </Section>
      ) : null}

      <Section title="Pattern Snapshot">
        <Body>{content.perspectiveSnapshot}</Body>
      </Section>

      <Section title="Mindset Operating Pattern">
        {/* Production frames this section per pattern before the payload copy. */}
        {PS_MINDSET_FRAMING[slug] ? (
          <Body testId="mindset-framing">{PS_MINDSET_FRAMING[slug]}</Body>
        ) : null}
        <Panel label="Focus and Attention Pattern">
          <Body>{content.focusControlPattern}</Body>
        </Panel>
      </Section>

      <Section title="How Your Mind Runs This Pattern">
        <Panel label="Self-Talk Pattern">
          <Body>{content.selfTalkPattern}</Body>
        </Panel>
        {PS_CONSCIOUS_VS_SUBCONSCIOUS[slug] ? (
          <Panel label="Conscious and Subconscious Pattern">
            <Body>{PS_CONSCIOUS_VS_SUBCONSCIOUS[slug]}</Body>
          </Panel>
        ) : null}
        <Panel label="Time Orientation">
          <Body>{content.timeOrientationPattern}</Body>
        </Panel>
        <Panel label="Belief Friction">
          <Body>{content.beliefFriction}</Body>
        </Panel>
      </Section>

      {hiddenDemand ? (
        <Section title="The Demand Behind the Pattern">
          <Body>
            Every cognitive pattern has a hidden demand underneath it. A demand is an irrational
            requirement that the mind treats as a condition for safety, progress, or self-worth. It
            shows up as a must, should, need, always, never, or cannot. The demand is not the same
            as a preference or a value. It is a non-negotiable internal requirement that creates
            friction when the situation does not satisfy it.
          </Body>
          <div
            className="flex flex-col gap-2 rounded-xl border border-amber-500/30 bg-amber-500/5 p-6"
            data-testid="perspective-hidden-demand"
          >
            <Eyebrow tone="text-amber-600 dark:text-amber-500">Your Hidden Demand</Eyebrow>
            <Body>{hiddenDemand}</Body>
          </div>
        </Section>
      ) : null}

      <Section title="Where This Pattern Helps and Where It Costs">
        <Body>
          Every cognitive pattern has contexts where it creates an advantage and contexts where the
          same tendency becomes a liability. The goal is not to eliminate the pattern. The goal is
          to understand both sides clearly enough to deploy it deliberately and catch it early when
          it is running past where it is useful.
        </Body>
        {content.patternStrengths.length > 0 ? (
          <div className="flex flex-col gap-3">
            <Eyebrow tone="text-green-600 dark:text-green-500">Where This Pattern Helps</Eyebrow>
            <div className="flex flex-col gap-3 rounded-xl border border-green-500/20 bg-green-500/5 p-5">
              {content.patternStrengths.map((item) => (
                <Body key={item}>{item}</Body>
              ))}
            </div>
          </div>
        ) : null}
        {content.costWhenOverused.length > 0 ? (
          <div className="flex flex-col gap-3">
            <Eyebrow tone="text-destructive/70">Where This Pattern Costs</Eyebrow>
            <div className="flex flex-col gap-3 rounded-xl border border-destructive/20 bg-destructive/5 p-5">
              {content.costWhenOverused.map((item) => (
                <Body key={item}>{item}</Body>
              ))}
            </div>
          </div>
        ) : null}
      </Section>

      {secondaryLabel ? (
        <Section title="Secondary Pattern and Combined Effect">
          <Panel label="Secondary Pattern">
            <p className="text-sm font-semibold">{secondaryLabel}</p>
          </Panel>
          {secondaryKey && PS_SECONDARY_AS_MODIFIER[secondaryKey] ? (
            <Panel label="Combined Effect">
              <Body>{PS_SECONDARY_AS_MODIFIER[secondaryKey]}</Body>
            </Panel>
          ) : null}
        </Section>
      ) : null}

      <Section title="Interpretation Traps and Reframe">
        {PS_TRAP_PAGE_INTRO[slug] ? (
          <Body testId="trap-intro">{PS_TRAP_PAGE_INTRO[slug]}</Body>
        ) : null}
        {PS_SCREEN_INTERACTION_NOTE[slug] ? (
          <div className="flex flex-col gap-2 rounded-xl border border-border bg-card p-6">
            <Eyebrow>Pattern Interaction</Eyebrow>
            <Body>{PS_SCREEN_INTERACTION_NOTE[slug]}</Body>
          </div>
        ) : null}
        <Eyebrow>Your Pattern Traps</Eyebrow>
        <div className="flex flex-col gap-3">
          {interpretationTraps.map((trap) => (
            <div
              key={trap.questionId}
              className={`break-inside-avoid rounded-lg border p-4 ${
                trap.active ? 'border-primary bg-card' : 'border-border'
              }`}
              data-testid="perspective-trap"
              data-active={trap.active}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium">{trap.label}</span>
                <span className="text-xs uppercase tracking-wide text-muted-foreground">
                  {trap.active ? 'Active' : 'Not active'}
                </span>
              </div>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                {trap.description}
              </p>
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-2 rounded-xl border border-primary/20 bg-primary/5 p-6">
          <Eyebrow tone="text-primary">Immediate Reframe</Eyebrow>
          <Body>{content.immediateReframe}</Body>
        </div>
      </Section>

      {oldLens || operatingStatement || watchFor ? (
        <Section title="Your Operating Statement">
          <Body>
            The operating statement is not a positive affirmation. A positive affirmation asserts
            what you wish were true. An operating statement asserts what is actually more accurate
            and more useful than the old lens. Your subconscious accepts what it recognizes as true.
            That is what makes this statement usable when the pattern activates.
          </Body>

          {oldLens ? (
            <div
              className="flex flex-col gap-2 rounded-xl border border-border bg-card p-6"
              data-testid="perspective-old-lens"
            >
              <Eyebrow>Old Lens</Eyebrow>
              <p className="border-l-4 border-border pl-4 text-sm italic text-muted-foreground">
                &ldquo;{oldLens}&rdquo;
              </p>
              <p className="pt-1 text-xs leading-relaxed text-muted-foreground">
                This is the internal sentence that typically runs when your dominant pattern
                activates. Recognizing this exact sentence is the prerequisite for replacing it.
              </p>
            </div>
          ) : null}

          {operatingStatement ? (
            <div
              className="flex flex-col gap-3 rounded-xl border border-primary/20 bg-primary/5 p-6"
              data-testid="perspective-operating-statement"
            >
              <Eyebrow tone="text-primary">Your Operating Statement</Eyebrow>
              <p className="text-lg font-bold">&ldquo;{operatingStatement}&rdquo;</p>
              <Body>
                This statement was built from what is actually more accurate about how you can
                operate. Practice saying it before pressure arrives, not only after. The old lens is
                strong because it has been practiced for years. The operating statement becomes
                strong the same way: through deliberate repetition until it is the faster automatic
                response.
              </Body>
            </div>
          ) : null}

          {watchFor ? (
            <div
              className="flex flex-col gap-2 rounded-xl border border-amber-500/30 bg-amber-500/5 p-5"
              data-testid="perspective-watch-for"
            >
              <Eyebrow tone="text-amber-600 dark:text-amber-500">What to Watch For</Eyebrow>
              <Body>{watchFor}</Body>
            </div>
          ) : null}
        </Section>
      ) : null}

      {/* Production letters these, and calls E "Effective New Belief". */}
      <Section title="ABCDE: Testing a Belief in Practice">
        {(
          [
            ['A — Activating Event', abcdeExample.activatingEvent],
            ['B — Belief', abcdeExample.belief],
            ['C — Consequence', abcdeExample.consequence],
            ['D — Disputation', abcdeExample.disputation],
            ['E — Effective New Belief', abcdeExample.newEffect],
          ] as const
        )
          .filter(([, content]) => content)
          .map(([label, content]) => (
            <Panel key={label} label={label}>
              <Body>{content}</Body>
            </Panel>
          ))}
      </Section>

      {/* Production frames the two workbooks as three numbered steps. */}
      <Section title="From Insight to Practice">
        <div className="flex flex-col gap-2">
          <Eyebrow>Step 1: 7-Day Perspective Reset</Eyebrow>
          <Body>
            The 7-Day Reset is a structured daily practice for interrupting your dominant pattern in
            real time. Each day focuses on catching the pattern as it activates, separating the
            situation from what your mind is adding to it, and applying the ABCDE model to a live
            moment. By the end of day 7, you will have tested the operating statement in real
            conditions and identified the specific triggers where your pattern activates most
            predictably.
          </Body>
        </div>
        <SevenDayWorkbook plan={sevenDayReset} />

        <div className="flex flex-col gap-2">
          <Eyebrow>Step 2: 30-Day New Mindset Practice</Eyebrow>
          <Body>
            The 30-Day Practice builds on the 7-Day Reset by moving from interruption to
            installation. The four weeks build on each other: noticing the pattern, replacing the
            response, testing it under pressure, and rehearsing the new behavior before the next
            high-stakes moment arrives. The weekly structure gives you a theme, a behavioral target,
            and journal prompts that connect each week&rsquo;s practice to the operating statement.
          </Body>
        </div>
        <ThirtyDayWorkbook plan={thirtyDayPractice} />

        <div className="flex flex-col gap-2" data-testid="perspective-step-3">
          <Eyebrow>Step 3: Reassess in 30 Days</Eyebrow>
          <Body>
            Return to the Perspective Assessment after completing both workbooks. Cognitive pattern
            scores shift when the operating pattern changes. Comparing your new result to this one
            shows where movement has occurred and where the next round of practice should focus.
          </Body>
        </div>
      </Section>

      <p className="text-xs text-muted-foreground">{disclaimer}</p>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-4" data-testid="perspective-section" data-title={title}>
      <h2 className="text-xl font-semibold">{title}</h2>
      {children}
    </section>
  );
}

function Eyebrow({
  children,
  tone = 'text-muted-foreground',
}: {
  children: React.ReactNode;
  tone?: string;
}) {
  return <p className={`text-xs font-bold uppercase tracking-widest ${tone}`}>{children}</p>;
}

function Panel({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2 rounded-xl border border-border bg-card p-5">
      <Eyebrow>{label}</Eyebrow>
      {children}
    </div>
  );
}

function Body({
  children,
  testId,
}: {
  children: React.ReactNode;
  testId?: string;
}) {
  return (
    <p
      className="text-sm leading-relaxed text-muted-foreground"
      {...(testId ? { 'data-testid': testId } : {})}
    >
      {children}
    </p>
  );
}
